import React from "react"
import type {
  ReactNode,
  CSSProperties,
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  ClipboardEventHandler
} from "react"
import cx from "classnames"

const STATES = {
  default: "border-gray-200 focus:outline-blue",
  error: "border-red-600 outline-red",
  success: "border-green-600 outline-green"
} as const

type PendProps = {
  item?: ReactNode
  className?: string
}

function Pend({ item, className }: PendProps) {
  if (!item) return null
  const classes = `text-gray-700 whitespace-pre flex items-center ${className}`

  return <div className={classes}>{item}</div>
}

export type InputProps = {
  /** Name attribute for the input element */
  name?: string
  /** Visual state of the input border and outline */
  state?: "default" | "error" | "success"
  /** Additional CSS classes for the input wrapper */
  className?: string
  /** Inline styles applied to the inner input element */
  style?: CSSProperties
  /** Content rendered after the input (e.g., icon or unit label) */
  append?: ReactNode
  /** Content rendered before the input (e.g., icon or prefix) */
  prepend?: ReactNode
  /** HTML id attribute */
  id?: string
  /** HTML input type (e.g., "text", "email", "number", "hidden") */
  type?: string
  /** Controlled value of the input */
  value?: string | number | readonly string[]
  /** Default value for uncontrolled usage */
  defaultValue?: string | number | readonly string[]
  /** Placeholder text shown when the input is empty */
  placeholder?: string
  /** Whether the input is required */
  required?: boolean
  /** Whether the input is disabled */
  disabled?: boolean
  /** Whether the input is read-only */
  readOnly?: boolean
  /** HTML autocomplete attribute */
  autoComplete?: string
  /** Whether the input should be focused on mount */
  autoFocus?: boolean
  /** Minimum value for number inputs */
  min?: number | string
  /** Maximum value for number inputs */
  max?: number | string
  /** Maximum character length */
  maxLength?: number
  /** Tab order of the input */
  tabIndex?: number
  /** Called when the input value changes */
  onChange?: ChangeEventHandler<HTMLInputElement>
  /** Called when the input loses focus */
  onBlur?: FocusEventHandler<HTMLInputElement>
  /** Called when the input gains focus */
  onFocus?: FocusEventHandler<HTMLInputElement>
  /** Called when a key is pressed */
  onKeyPress?: KeyboardEventHandler<HTMLInputElement>
  /** Called when a key is pressed down */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
  /** Called when content is pasted */
  onPaste?: ClipboardEventHandler<HTMLInputElement>
  /** Accessible label for the input */
  "aria-label"?: string
  /** ID of the element that describes the input */
  "aria-describedby"?: string
  /** Whether the input value is invalid */
  "aria-invalid"?: boolean | "true" | "false"
  /** @deprecated Form system attribute */
  __type?: string
  /** @deprecated Form system attribute */
  initialstate?: string
  /** @deprecated Form system attribute */
  validation_dependencies?: string
}

/**
 * A text input field with support for validation states, prepend/append slots,
 * and forwarded refs. Renders as a hidden input when type is "hidden".
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { name, state = "default", className, style, append, prepend, ...props },
    ref
  ) => {
    className = cx(
      className,
      "flex text-sm border rounded px-3 py-2 w-full bg-white",
      STATES[state]
    )

    if (props.type === "hidden") {
      return <input ref={ref} name={name} {...props} />
    }

    const ignoreAutofillAttrs = props.autoComplete === "off"
      ? { "data-lpignore": "true", "data-1p-ignore": "true" }
      : {}

    return (
      <div className={className}>
        <Pend item={prepend} className="pr-2" />
        <input
          ref={ref}
          style={{ lineHeight: "22px", ...style }}
          className="w-full focus:outline-none design-input"
          name={name}
          {...ignoreAutofillAttrs}
          {...props}
        />
        <Pend item={append} className="pl-2" />
      </div>
    )
  }
)

export { Input }
export default Input
